import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  audits as auditsApi,
  checklist as checklistApi,
  files as filesApi,
  templates as templatesApi,
} from "../lib/api";
import { computeScore } from "../lib/score";
import { setFor, flaggedDefaultFor, TONE_CLASSES } from "../lib/responseSets";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import SemaforoBadge from "../components/SemaforoBadge";
import FileUploader from "../components/FileUploader";
import SignaturePad from "../components/SignaturePad";
import ExcelUploader from "../components/ExcelUploader";
import MultaDialog from "../components/MultaDialog";
import {
  CheckCheck,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  DollarSign,
  RotateCcw,
  Check,
  X,
  BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useApp } from "../context/AppContext";
import { pickField, detectHeaders } from "../lib/excel";
import { localizedQuestion, localizedTemplate, localizedCategory, localizedSeverity, templateColor } from "../lib/i18nContent";

const RESPONSE_OPTIONS = [
  { v: "yes", icon: Check, classes: "is-active yes" },
  { v: "no", icon: X, classes: "is-active no" },
  { v: "en_proceso", icon: RotateCcw, classes: "is-active en-proceso" },
  { v: "warning", icon: AlertTriangle, classes: "is-active warning" },
  { v: "multa", icon: DollarSign, classes: "is-active multa" },
];

// Map legacy responses to the new vocabulary so existing audits keep working
function normalizeResponse(v) {
  if (v === "cumple") return "yes";
  if (v === "no_cumple") return "no";
  return v;
}

const DEFAULT_FLAGGED = ["no", "en_proceso", "warning", "multa"];

// Return the first per-question logic rule that matches the given response.
function activeRuleFor(q, response) {
  if (!q || !response) return null;
  for (const rule of q.logic || []) {
    if ((rule.on || []).includes(response)) return rule;
  }
  return null;
}

// Resolve what a given response requires, honoring per-question logic when set
// and falling back to the historical default (flagged answers require photo +
// comment; No/Multa auto-create a corrective action).
function requirementFor(q, response) {
  const rule = activeRuleFor(q, response);
  if (rule) {
    const tr = rule.triggers || {};
    return {
      rule, tr,
      instruction: (tr.instruction || "").trim(),
      requirePhoto: !!tr.require_photo || !!tr.block_close,
      requireComment: !!tr.require_comment || !!tr.block_close,
      followupQuestion: (tr.followup_question || "").trim(),
      followupRequired: !!tr.followup_required && !!(tr.followup_question || "").trim(),
      blockClose: !!tr.block_close,
      createAction: !!tr.create_action,
    };
  }
  const flagged = new Set(q.flagged_responses == null ? flaggedDefaultFor(q.response_type || "canales") : q.flagged_responses).has(response);
  const rt = q.response_type || "canales";
  return {
    rule: null, tr: null, instruction: "",
    requirePhoto: flagged, requireComment: flagged,
    followupQuestion: "", followupRequired: false, blockClose: false,
    createAction: rt === "canales" ? (response === "no" || response === "multa") : flagged,
  };
}

export default function AuditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useApp();

  const [audit, setAudit] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [tplList, setTplList] = useState([]);
  const [responses, setResponses] = useState({}); // qId -> { response, comment, photo_url, photo_path, multa_amount, multa_reason }
  const [photosByQ, setPhotosByQ] = useState({});
  const catRefs = useRef({});
  const [savingId, setSavingId] = useState(null);
  const [multaCtx, setMultaCtx] = useState(null); // { question } when dialog open

  // Autosave: keep a live ref of responses + per-question debounce timers so we
  // can persist comments / follow-up / text-number fields ~1s after typing stops.
  const responsesRef = useRef(responses);
  useEffect(() => { responsesRef.current = responses; }, [responses]);
  const saveTimers = useRef({});
  useEffect(() => () => { Object.values(saveTimers.current).forEach(clearTimeout); }, []);

  const scheduleAutosave = (q, field) => {
    clearTimeout(saveTimers.current[q.id]);
    saveTimers.current[q.id] = setTimeout(() => {
      const cur = responsesRef.current[q.id];
      if (!cur) return;
      if (field === "data") {
        const has = (cur.comment || "").trim();
        persistResponse(q, { comment: cur.comment || "", response: has ? (q.response_type || "text") : "" });
      } else {
        if (!cur.response) return; // need an answer before saving comment/follow-up
        persistResponse(q, field === "followup"
          ? { followup_answer: cur.followup_answer || "" }
          : { comment: cur.comment || "" });
      }
    }, 1000);
  };

  useEffect(() => {
    templatesApi.list().then(setTplList).catch(console.error);
  }, []);

  const loadAll = async () => {
    const data = await auditsApi.get(id);
    setAudit(data.audit);
    const r = {};
    const p = {};
    for (const it of data.responses || []) {
      r[it.question_id] = {
        response: normalizeResponse(it.response),
        comment: it.comment || "",
        photo_url: it.photo_url || "",
        photo_path: it.photo_path || "",
        multa_amount: it.multa_amount ?? null,
        multa_reason: it.multa_reason || "",
        followup_answer: it.followup_answer || "",
      };
      if (it.photo_url) {
        p[it.question_id] = [{ url: it.photo_url, original_filename: "evidencia.jpg" }];
      }
    }
    setResponses(r);
    setPhotosByQ(p);
    const ck = await checklistApi.list();
    setQuestions(ck);
  };

  useEffect(() => {
    loadAll();
  }, [id]);

  // Filter checklist to the questions belonging to the audit's chosen templates (1+ supported).
  const auditTemplates = useMemo(() => {
    if (!audit) return [];
    if (Array.isArray(audit.templates) && audit.templates.length) return audit.templates;
    if (audit.template) return [audit.template];
    return [];
  }, [audit]);

  const templateQuestions = useMemo(() => {
    if (!audit) return [];
    if (auditTemplates.length === 0) return questions;
    const set = new Set(auditTemplates);
    return questions.filter((q) => set.has(q.template || "Tienda"));
  }, [questions, audit, auditTemplates]);

  const isMulti = auditTemplates.length > 1;
  const tabKey = (q) => (isMulti ? `${q.template || "Tienda"} › ${q.category}` : q.category);

  const categories = useMemo(
    () => [...new Set(templateQuestions.map(tabKey))],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateQuestions, isMulti],
  );

  // Delegate to `localizedCategory`, which now consults a runtime lookup
  // populated from every checklist question's `i18n.{es,en}.category` cache
  // (registered when the checklist is fetched). Handles both directions and
  // any user-added categories that aren't in the static seed map.
  const catLocalized = (raw) => localizedCategory(raw, i18n.language);

  const questionsByCat = useMemo(() => {
    const m = {};
    for (const q of templateQuestions) {
      const k = tabKey(q);
      m[k] = m[k] || [];
      m[k].push(q);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateQuestions, isMulti]);

  // Global running number across the single continuous list
  const qNumber = useMemo(() => {
    const m = {};
    let n = 0;
    for (const c of categories) {
      for (const q of questionsByCat[c] || []) m[q.id] = ++n;
    }
    return m;
  }, [categories, questionsByCat]);

  const scrollToQuestion = (qid) => {
    const el = document.querySelector(`[data-testid="question-${qid}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Unanswered question ids, in list order
  const unansweredIds = useMemo(() => {
    const ids = [];
    for (const c of categories) {
      for (const q of questionsByCat[c] || []) {
        if (!responses[q.id]?.response) ids.push(q.id);
      }
    }
    return ids;
  }, [categories, questionsByCat, responses]);

  const goNextUnanswered = () => {
    if (!unansweredIds.length) return;
    const marker = window.scrollY + window.innerHeight / 2;
    for (const qid of unansweredIds) {
      const el = document.querySelector(`[data-testid="question-${qid}"]`);
      if (el && el.getBoundingClientRect().top + window.scrollY > marker + 20) {
        scrollToQuestion(qid);
        return;
      }
    }
    scrollToQuestion(unansweredIds[0]);
  };

  const score = useMemo(() => {
    const list = Object.entries(responses).map(([qid, v]) => ({ question_id: qid, ...v }));
    return computeScore(list, templateQuestions);
  }, [responses, templateQuestions]);

  const answeredCount = Object.entries(responses).filter(
    ([qid, v]) => v.response && templateQuestions.some((q) => q.id === qid),
  ).length;
  const totalQ = templateQuestions.length;

  const persistResponse = async (q, partial) => {
    const cur = responses[q.id] || {};
    const next = { ...cur, ...partial };
    setResponses((s) => ({ ...s, [q.id]: next }));
    if (!next.response) return next; // nothing to save until a response is set
    try {
      setSavingId(q.id);
      await auditsApi.saveResponse(id, {
        question_id: q.id,
        category: q.category,
        question: q.question,
        weight: q.weight,
        criticality: q.criticality,
        template: q.template || audit?.template || "",
        response: next.response,
        comment: next.comment || "",
        photo_url: next.photo_url || "",
        photo_path: next.photo_path || "",
        multa_amount: next.multa_amount ?? null,
        multa_reason: next.multa_reason || "",
        followup_answer: next.followup_answer || "",
      });
      // NOTE: the corrective action (finding) is now created server-side in
      // POST /audits/{id}/responses so it can never be lost if the browser
      // navigates away mid-save. Idempotent per (audit_id, question_id).
    } catch (e) {
      console.error(e);
      toast.error(t("audit.save_error"));
    } finally {
      setSavingId(null);
    }
    return next;
  };

  const setResponse = (q, val) => {
    if (val === "multa") {
      setMultaCtx({ question: q });
      return;
    }
    persistResponse(q, { response: val });
  };

  const onMultaConfirmed = async ({ amount, reason }) => {
    if (!multaCtx?.question) return;
    await persistResponse(multaCtx.question, {
      response: "multa",
      multa_amount: amount,
      multa_reason: reason,
    });
    setMultaCtx(null);
  };

  const setComment = (q, val) => {
    setResponses((s) => ({ ...s, [q.id]: { ...(s[q.id] || {}), comment: val } }));
    scheduleAutosave(q, "comment");
  };

  const saveComment = async (q) => {
    const cur = responses[q.id];
    if (!cur || !cur.response) return;
    await persistResponse(q, { comment: cur.comment || "" });
    toast.success(t("audit.comment_saved"));
  };

  const setFollowup = (q, val) => {
    setResponses((s) => ({ ...s, [q.id]: { ...(s[q.id] || {}), followup_answer: val } }));
    scheduleAutosave(q, "followup");
  };

  const saveFollowup = async (q) => {
    const cur = responses[q.id];
    if (!cur || !cur.response) return;
    await persistResponse(q, { followup_answer: cur.followup_answer || "" });
  };

  const onPhotoUploaded = async (q, uploaded) => {
    const first = uploaded[0];
    if (!first) return;
    const url = filesApi.url(first.id);
    setPhotosByQ((s) => ({
      ...s,
      [q.id]: [...(s[q.id] || []), { ...first, url }],
    }));
    await persistResponse(q, {
      photo_url: url,
      photo_path: first.storage_path,
      response: responses[q.id]?.response || "no",
    });
  };

  // ---- Data-capture field types (text / number / media / signature) ----
  const setDataText = (q, val) => {
    const rt = q.response_type || "text";
    setResponses((s) => ({
      ...s,
      [q.id]: { ...(s[q.id] || {}), comment: val, response: val.trim() ? rt : "" },
    }));
    scheduleAutosave(q, "data");
  };

  const saveData = async (q) => {
    const cur = responses[q.id];
    if (!cur) return;
    const has = (cur.comment || "").trim();
    await persistResponse(q, { comment: cur.comment || "", response: has ? (q.response_type || "text") : "" });
  };

  const onMediaUploaded = async (q, uploaded) => {
    const first = uploaded[0];
    if (!first) return;
    const url = filesApi.url(first.id);
    await persistResponse(q, { photo_url: url, photo_path: first.storage_path, response: "media" });
  };

  const setSigData = (q, dataUrl) => {
    setResponses((s) => ({ ...s, [q.id]: { ...(s[q.id] || {}), _sig: dataUrl } }));
  };

  const saveSignature = async (q) => {
    const cur = responses[q.id];
    const d = cur?._sig;
    if (!d) return toast.error(t("audit.signature_empty"));
    try {
      const blob = await (await fetch(d)).blob();
      const file = new File([blob], `signature-${q.id}.png`, { type: "image/png" });
      const rec = await filesApi.upload(file, id, "photo");
      await persistResponse(q, { photo_url: filesApi.url(rec.id), photo_path: rec.storage_path || "", response: "signature" });
      toast.success(t("audit.signature_saved"));
    } catch {
      toast.error(t("common.error_generic"));
    }
  };

  const onChecklistImport = async (rows) => {
    const items = rows
      .map((r) => ({
        template: String(pickField(r, "Plantilla", "Template", "Tipo", "Tipo de auditoría") || "Tienda").trim() || "Tienda",
        category: String(pickField(r, "Categoria", "Categoría", "Category", "Cat") || "").trim(),
        question: String(pickField(r, "Pregunta", "Question", "Q", "Item", "Reactivo") || "").trim(),
        weight: parseFloat(pickField(r, "Peso", "Weight", "W", "Ponderacion", "Ponderación")) || 1,
        criticality: String(pickField(r, "Criticidad", "Criticality", "Critico", "Crítico", "Severity", "Severidad") || "Media").trim(),
      }))
      .filter((x) => x.category && x.question);
    if (!items.length) {
      const detected = detectHeaders(rows).join(", ") || "—";
      toast.error(`${t("audit.checklist_headers_error")} · Detectado: ${detected}`);
      return;
    }
    const validCrit = (v) => {
      const n = v.toLowerCase();
      if (["alta", "high", "h", "1"].includes(n)) return "Alta";
      if (["baja", "low", "l", "3"].includes(n)) return "Baja";
      return "Media";
    };
    items.forEach((x) => { x.criticality = validCrit(x.criticality); });
    await checklistApi.bulk(items, true);
    toast.success(t("audit.checklist_updated", { n: items.length }));
    const ck = await checklistApi.list();
    setQuestions(ck);
  };

  // Compute which questions need evidence but are missing photo, comment or a
  // required follow-up answer — honoring per-question logic rules.
  const incompleteEvidence = useMemo(() => {
    const missing = [];
    for (const q of templateQuestions) {
      const r = responses[q.id];
      if (!r || !r.response) continue;
      const req = requirementFor(q, r.response);
      const noComment = req.requireComment && !(r.comment || "").trim();
      const noPhoto = req.requirePhoto && !((r.photo_url || "").trim());
      const noFollowup = req.followupRequired && !((r.followup_answer || "").trim());
      if (noComment || noPhoto || noFollowup) {
        missing.push({ q, noComment, noPhoto, noFollowup });
      }
    }
    return missing;
  }, [templateQuestions, responses]);

  const closeAudit = async () => {
    if (incompleteEvidence.length > 0) {
      const first = incompleteEvidence[0];
      toast.error(
        t("audit.evidence_missing_close", {
          n: incompleteEvidence.length,
          q: first.q.question.slice(0, 60),
        }),
      );
      // Jump straight to the first failing question in the list
      scrollToQuestion(first.q.id);
      return;
    }
    const unanswered = totalQ - answeredCount;
    if (unanswered > 0) {
      if (!window.confirm(t("audit.confirm_close", { n: unanswered }))) return;
    }
    await auditsApi.patch(id, {
      status: "closed",
      score: score.score,
      score_obtained: score.obtained,
      score_evaluated: score.evaluated,
    });
    toast.success(t("audit.audit_closed"));
    navigate(`/summary/${id}`);
  };

  if (!audit) {
    return <div className="p-10 text-stone-500">{t("audit.loading")}</div>;
  }

  const isClosed = audit.status === "closed";
  const noChecklist = templateQuestions.length === 0;

  return (
    <div className="p-4 lg:p-10 max-w-[1500px] mx-auto" data-testid="audit-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="canales-overline">{t("audit.in_field")}</div>
          <h1 className="font-display text-3xl md:text-4xl mt-1">{audit.store_name}</h1>
          <p className="text-stone-600 text-sm mt-1">
            {audit.distrito} · {audit.ciudad} · {t("audit.auditor_label")}: {audit.auditor_name}
          </p>
          {(audit.template || audit.audit_number || audit.manager_name || (audit.templates && audit.templates.length > 1)) && (
            <div className="flex flex-wrap gap-2 mt-2">
              {auditTemplates.map((tplName) => (
                <span key={tplName} className="pill text-xs inline-flex items-center gap-1.5" data-testid={`audit-template-badge-${tplName}`}>
                  <span
                    className="inline-block w-2 h-2 rounded-full ring-1 ring-white/60 shrink-0"
                    style={{ backgroundColor: templateColor(tplName, tplList) }}
                    aria-hidden
                  />
                  {t("audit.template_label")}: <strong className="ml-0.5">{localizedTemplate(tplName, i18n.language)}</strong>
                </span>
              ))}
              {audit.audit_number && (
                <span className="pill text-xs">{audit.audit_number}</span>
              )}
              {audit.manager_name && (
                <span className="pill text-xs">{t("cover.manager_name")}: {audit.manager_name}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <SemaforoBadge score={score.score} semaforo={score.semaforo} size="lg" />
          <Button
            onClick={closeAudit}
            disabled={isClosed || noChecklist}
            className="brand-button-accent gap-2"
            data-testid="close-audit-button"
          >
            <CheckCheck size={16} /> {isClosed ? t("audit.closed") : t("audit.close")}
          </Button>
        </div>
      </div>

      {/* Progress */}
      <Card className="canales-card mt-6 p-5">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-stone-600">
            {t("audit.progress", { answered: answeredCount, total: totalQ })}
          </span>
          <span className="font-display text-lg">{score.score}%</span>
        </div>
        <Progress value={totalQ ? (answeredCount / totalQ) * 100 : 0} />
      </Card>

      {incompleteEvidence.length > 0 && !isClosed && (
        <Card className="mt-4 p-4 border-l-4 border-red-500 bg-red-50/60" data-testid="evidence-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-500 mt-0.5" size={18} />
            <div className="text-sm flex-1">
              <p className="font-semibold text-red-700">
                {t("audit.evidence_banner_title", { n: incompleteEvidence.length })}
              </p>
              <p className="text-red-600 text-xs mt-1">
                {t("audit.evidence_banner_desc")}
              </p>
              <button
                type="button"
                onClick={() => scrollToQuestion(incompleteEvidence[0]?.q?.id)}
                className="text-xs underline text-red-700 hover:text-red-900 mt-1.5"
                data-testid="evidence-jump-button"
              >
                {t("audit.evidence_jump")}
              </button>
            </div>
          </div>
        </Card>
      )}

      {noChecklist ? (
        <Card className="canales-card mt-6 p-8 text-center">
          <h3 className="font-display text-xl">{t("audit.no_questions_in_template")}</h3>
          <p className="text-stone-600 text-sm mt-2">
            {t("audit.no_checklist_desc")}
          </p>
          <div className="mt-4 flex justify-center">
            <ExcelUploader onParsed={onChecklistImport} testid="checklist-import" label={t("audit.upload_checklist")} />
          </div>
        </Card>
      ) : (
        <div className="mt-6" data-testid="audit-question-list">
          {/* Quick-nav chips: scroll to each topic inside the single continuous list */}
          <div className="flex flex-wrap gap-1 bg-stone-100 p-1 rounded-lg sticky top-16 z-20 shadow-sm max-h-28 overflow-y-auto" data-testid="category-tabs">
            {categories.map((c) => {
              const cat = score.categories.find((x) => x.category === c);
              // `c` is the raw key (either "<Category>" or "<Template> › <Category>").
              // Translate each segment so tab labels follow the active language.
              const parts = String(c).split(" › ");
              const displayLabel = parts.length === 2
                ? `${localizedTemplate(parts[0], i18n.language)} › ${catLocalized(parts[1])}`
                : catLocalized(c);
              const dotColor = parts.length === 2 ? templateColor(parts[0], tplList) : null;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => catRefs.current[c]?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="inline-flex items-center rounded-md px-3 py-1.5 text-xs sm:text-sm font-medium bg-white/70 hover:bg-white transition-colors"
                  data-testid={`tab-${c}`}
                >
                  {dotColor && (
                    <span
                      className="inline-block w-2 h-2 rounded-full ring-1 ring-white/60 mr-1.5 shrink-0"
                      style={{ backgroundColor: dotColor }}
                      aria-hidden
                    />
                  )}
                  <span>{displayLabel}</span>
                  {cat && (
                    <span
                      className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${
                        cat.semaforo === "verde"
                          ? "bg-emerald-100 text-emerald-700"
                          : cat.semaforo === "amarillo"
                          ? "bg-amber-100 text-amber-700"
                          : cat.semaforo === "rojo"
                          ? "bg-red-100 text-red-700"
                          : "bg-stone-200 text-stone-600"
                      }`}
                    >
                      {cat.subtotal.toFixed(0)}%
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {categories.map((c) => {
            const cat = score.categories.find((x) => x.category === c);
            const parts = String(c).split(" › ");
            const displayLabel = parts.length === 2
              ? `${localizedTemplate(parts[0], i18n.language)} › ${catLocalized(parts[1])}`
              : catLocalized(c);
            const sectionColor = parts.length === 2 ? templateColor(parts[0], tplList) : null;
            return (
            <section
              key={c}
              ref={(el) => (catRefs.current[c] = el)}
              className="mt-8 space-y-4 scroll-mt-48"
              data-testid={`category-section-${c}`}
            >
              <div className="flex items-center gap-2 border-b border-stone-200 pb-2">
                {sectionColor && (
                  <span
                    className="inline-block w-1.5 h-6 rounded-full shrink-0"
                    style={{ backgroundColor: sectionColor }}
                    aria-hidden
                  />
                )}
                <h3 className="font-display text-lg md:text-xl">{displayLabel}</h3>
                {cat && (
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full ${
                      cat.semaforo === "verde"
                        ? "bg-emerald-100 text-emerald-700"
                        : cat.semaforo === "amarillo"
                        ? "bg-amber-100 text-amber-700"
                        : cat.semaforo === "rojo"
                        ? "bg-red-100 text-red-700"
                        : "bg-stone-200 text-stone-600"
                    }`}
                  >
                    {cat.subtotal.toFixed(0)}%
                  </span>
                )}
              </div>
              {(questionsByCat[c] || []).map((q, idx) => {
                const r = responses[q.id] || {};
                const rt = q.response_type || "canales";
                const rs = setFor(rt);
                const dataCapture = !rs.scored;
                // Comment + photo fields show for answered SCORED questions. They are
                // REQUIRED per the question's logic (or the default flagged set).
                const showFeedback = !!r.response && rs.scored;
                const req = requirementFor(q, r.response);
                const noComment = req.requireComment && !(r.comment || "").trim();
                const noPhoto = req.requirePhoto && !((r.photo_url || "").trim());
                const noFollowup = req.followupRequired && !((r.followup_answer || "").trim());
                const evidenceMissing = noComment || noPhoto || noFollowup;
                const lq = localizedQuestion(q, i18n.language);
                return (
                  <Card
                    key={q.id}
                    className={`canales-card p-5 ${evidenceMissing ? "ring-2 ring-red-300 ring-offset-2" : ""}`}
                    data-testid={`question-${q.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-stone-500">
                          <span>#{qNumber[q.id]}</span>
                          <span>·</span>
                          <span>{t("audit.weight_label")} {q.weight}</span>
                          <span>·</span>
                          <span
                            className={`font-medium ${
                              q.criticality === "Alta"
                                ? "text-red-600"
                                : q.criticality === "Baja"
                                ? "text-stone-500"
                                : "text-amber-600"
                            }`}
                          >
                            {t("audit.criticality_label")} {localizedSeverity(q.criticality, i18n.language)}
                          </span>
                        </div>
                        <h4 className="font-medium mt-1">{lq.question}</h4>
                        {r.response === "multa" && (
                          <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs">
                            <DollarSign size={12} />
                            <span className="font-semibold">
                              {r.multa_amount ? `$${Number(r.multa_amount).toLocaleString()}` : t("audit.multa")}
                            </span>
                            {r.multa_reason && (
                              <span className="truncate max-w-[280px] text-red-600/80">· {r.multa_reason}</span>
                            )}
                            <button
                              type="button"
                              className="ml-1 underline hover:text-red-900"
                              onClick={() => setMultaCtx({ question: q })}
                            >
                              {t("common.edit")}
                            </button>
                          </div>
                        )}
                      </div>
                      {savingId === q.id && <span className="text-xs text-stone-400">{t("audit.saving")}</span>}
                    </div>

                    {rs.scored && rt === "canales" && (
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-4" data-testid={`response-toggle-${q.id}`}>
                        {RESPONSE_OPTIONS.map((opt) => {
                          const Icon = opt.icon;
                          const active = r.response === opt.v;
                          return (
                            <button
                              key={opt.v}
                              disabled={isClosed}
                              onClick={() => setResponse(q, opt.v)}
                              className={`checklist-toggle py-2.5 text-xs sm:text-sm flex items-center justify-center gap-1.5 ${
                                active ? opt.classes : ""
                              }`}
                              data-testid={`response-${opt.v}-${q.id}`}
                            >
                              <Icon size={14} />
                              <span>{t(`audit.${opt.v}`)}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {rs.scored && rt !== "canales" && (
                      <div className="flex flex-wrap gap-2 mt-4" data-testid={`response-toggle-${q.id}`}>
                        {rs.options.map((opt) => {
                          const active = r.response === opt.v;
                          return (
                            <button
                              key={opt.v}
                              disabled={isClosed}
                              onClick={() => setResponse(q, opt.v)}
                              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${
                                active ? TONE_CLASSES[opt.tone] : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
                              }`}
                              data-testid={`response-${opt.v}-${q.id}`}
                            >
                              {t(`audit.${opt.v}`)}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {dataCapture && (
                      <div className="mt-4" data-testid={`data-capture-${q.id}`}>
                        {rt === "text" && (
                          <Textarea
                            value={r.comment || ""}
                            disabled={isClosed}
                            onChange={(e) => setDataText(q, e.target.value)}
                            onBlur={() => saveData(q)}
                            placeholder={t("audit.text_answer_ph")}
                            className="min-h-[80px]"
                            data-testid={`data-text-${q.id}`}
                          />
                        )}
                        {rt === "number" && (
                          <Input
                            type="number"
                            value={r.comment || ""}
                            disabled={isClosed}
                            onChange={(e) => setDataText(q, e.target.value)}
                            onBlur={() => saveData(q)}
                            placeholder={t("audit.number_answer_ph")}
                            className="max-w-[220px]"
                            data-testid={`data-number-${q.id}`}
                          />
                        )}
                        {rt === "media" && (
                          <div className="flex items-center gap-3 flex-wrap">
                            <FileUploader
                              auditId={id}
                              kind="photo"
                              onUploaded={(u) => onMediaUploaded(q, u)}
                              testid={`data-media-${q.id}`}
                              label={t("audit.media_answer")}
                            />
                            {r.photo_url && (
                              <a href={r.photo_url} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded-lg overflow-hidden border border-stone-200">
                                <img src={r.photo_url} alt="" className="w-full h-full object-cover" />
                              </a>
                            )}
                          </div>
                        )}
                        {rt === "signature" && (
                          <div>
                            <SignaturePad value={r.response === "signature" ? (r.photo_url || "") : ""} onChange={(d) => setSigData(q, d)} testid={`data-sig-${q.id}`} />
                            <Button size="sm" className="brand-button gap-1.5 mt-2" disabled={isClosed} onClick={() => saveSignature(q)} data-testid={`data-sig-save-${q.id}`}>
                              <Check size={13} /> {t("audit.signature_save")}
                            </Button>
                            {r.photo_url && r.response === "signature" && (
                              <a href={r.photo_url} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs text-[#1E3A8A] underline" data-testid={`data-sig-view-${q.id}`}>
                                {t("audit.view_signature")}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {showFeedback && (
                      <div className="mt-4 space-y-3">
                        {req.instruction && (
                          <div
                            className="flex items-start gap-2 rounded-lg border-l-4 border-[#1E3A8A] bg-[#E5EBFA]/50 px-3 py-2"
                            data-testid={`instruction-${q.id}`}
                          >
                            <AlertTriangle size={15} className="text-[#1E3A8A] mt-0.5 shrink-0" />
                            <p className="text-sm text-[#1E3A8A]">{req.instruction}</p>
                          </div>
                        )}
                        {req.tr?.manual_file?.url && (
                          <div className="rounded-lg border border-stone-200 overflow-hidden" data-testid={`manual-${q.id}`}>
                            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-stone-50 border-b border-stone-200">
                              <span className="text-xs font-semibold text-stone-700 flex items-center gap-1.5 truncate">
                                <BookOpen size={13} /> {req.tr.manual_file.name || t("audit.manual")}
                              </span>
                              <a
                                href={req.tr.manual_file.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-[#1E3A8A] font-semibold underline shrink-0"
                                data-testid={`manual-open-${q.id}`}
                              >
                                {t("audit.view_manual")}
                              </a>
                            </div>
                            {(req.tr.manual_file.content_type || "").startsWith("image/") ? (
                              <img src={req.tr.manual_file.url} alt="" className="w-full max-h-72 object-contain bg-white" />
                            ) : (req.tr.manual_file.content_type || "").includes("pdf") ? (
                              <iframe title={`manual-${q.id}`} src={req.tr.manual_file.url} className="w-full h-72 bg-white" />
                            ) : null}
                          </div>
                        )}
                        <div>
                          <label className="canales-overline flex items-center gap-1.5">
                            {t("audit.comment")}
                            {req.requireComment && <span className="text-red-600">*</span>}
                          </label>
                          <Textarea
                            value={r.comment || ""}
                            disabled={isClosed}
                            onChange={(e) => setComment(q, e.target.value)}
                            onBlur={() => saveComment(q)}
                            placeholder={
                              req.requireComment
                                ? t("audit.comment_required")
                                : t("audit.comment_optional")
                            }
                            className={`mt-1 min-h-[72px] ${noComment ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                            data-testid={`comment-${q.id}`}
                          />
                          {noComment && (
                            <p className="text-xs text-red-600 mt-1" data-testid={`comment-required-error-${q.id}`}>
                              {t("audit.comment_required_error")}
                            </p>
                          )}
                        </div>
                        {req.followupQuestion && (
                          <div data-testid={`followup-wrap-${q.id}`}>
                            <label className="canales-overline flex items-center gap-1.5">
                              {req.followupQuestion}
                              {req.followupRequired && <span className="text-red-600">*</span>}
                            </label>
                            <Textarea
                              value={r.followup_answer || ""}
                              disabled={isClosed}
                              onChange={(e) => setFollowup(q, e.target.value)}
                              onBlur={() => saveFollowup(q)}
                              className={`mt-1 min-h-[60px] ${noFollowup ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                              data-testid={`followup-${q.id}`}
                            />
                            {noFollowup && (
                              <p className="text-xs text-red-600 mt-1" data-testid={`followup-required-error-${q.id}`}>
                                {t("audit.followup_required_error")}
                              </p>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <FileUploader
                            auditId={id}
                            kind="photo"
                            onUploaded={(u) => onPhotoUploaded(q, u)}
                            testid={`photo-${q.id}`}
                            label={
                              req.requirePhoto
                                ? t("audit.photo_required")
                                : t("audit.photo_optional")
                            }
                          />
                          {(photosByQ[q.id] || []).map((p, i) => (
                            <a
                              key={i}
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                              className="w-12 h-12 rounded-md border border-stone-200 overflow-hidden bg-stone-50 flex items-center justify-center"
                            >
                              <img src={p.url} alt="evidencia" className="w-full h-full object-cover" />
                            </a>
                          ))}
                          {noPhoto && (
                            <span className="text-xs text-red-600" data-testid={`photo-required-error-${q.id}`}>
                              {t("audit.photo_required_error")}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </section>
            );
          })}
        </div>
      )}

      {!noChecklist && (
        <div className="fixed bottom-5 left-5 z-30 flex flex-col gap-2" data-testid="audit-float-nav">
          {!isClosed && unansweredIds.length > 0 && (
            <button
              type="button"
              onClick={goNextUnanswered}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B1220] text-white text-xs sm:text-sm shadow-xl border border-white/10 hover:-translate-y-0.5 transition-transform"
              data-testid="next-unanswered-button"
            >
              <ArrowDown size={15} />
              <span>{t("audit.next_unanswered")}</span>
              <span className="ml-0.5 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-amber-400 text-stone-900 text-[11px] font-bold">
                {unansweredIds.length}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="self-start flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-stone-700 text-xs sm:text-sm shadow-lg border border-stone-200 hover:bg-stone-50 hover:-translate-y-0.5 transition-transform"
            data-testid="scroll-top-button"
          >
            <ArrowUp size={15} />
            <span>{t("audit.go_top")}</span>
          </button>
        </div>
      )}

      <MultaDialog
        open={!!multaCtx}
        onOpenChange={(v) => !v && setMultaCtx(null)}
        auditId={id}
        question={multaCtx?.question}
        category={multaCtx?.question?.category}
        template={audit?.template || multaCtx?.question?.template}
        store={audit?.store_name}
        initialAmount={
          multaCtx?.question ? responses[multaCtx.question.id]?.multa_amount ?? "" : ""
        }
        initialReason={
          multaCtx?.question ? responses[multaCtx.question.id]?.multa_reason ?? "" : ""
        }
        onConfirmed={onMultaConfirmed}
      />
    </div>
  );
}
